/**
 * A client's transport: the one typed API every request of a
 * `LonaSdk.Client` goes through, the sync client its status polls run on,
 * and its durable (debounced) writes bound to the host's executor — all
 * bound to the client's origin, token and `fetch` (`ClientConfig`). A
 * request the API tolerates (a cell-range read that reads empty on failure)
 * reports its failure to the host's `requestErrorHandler`. Built once, first,
 * by the client's constructor; holds nothing of the client but the liveness
 * check a durable write runs.
 */
import type { Option } from "@tento-lona/sheets";
import { TypedApi } from "@tento-core/typed-api";
import { Network } from "../network.ts";
import { SyncClient } from "../services/sync/sync-client.ts";
import { type DebounceExecutor, type RequestErrorHandler, tolerantRequestError } from "./client-seams.ts";

/** What a client's transport is: see `clientTransport`. */
export interface ClientTransport {
  /** `ClientConfig.baseUrl` without a trailing `/` (`null`: same-origin requests). */
  readonly baseUrl: Option<string>;
  /** The client's durable writes on the host's executor; the same binding reports their refusals. */
  readonly binding: DebounceExecutor.Binding;
  /** The typed API every request of the client goes through. */
  readonly api: TypedApi<Network.Routes>;
  /** The sync client the client's one paced sync-status poll runs on. */
  readonly syncClient: SyncClient;
}

export namespace ClientTransport {
  /** The `ClientConfig` fields a transport is built from. */
  export type Config = {
    readonly baseUrl: Option<string>;
    readonly accessToken: Option<string>;
    readonly fetch: Option<typeof fetch>;
    readonly debounceExecutor: DebounceExecutor;
    readonly requestErrorHandler: RequestErrorHandler;
  };
}

/**
 * The transport of a client built from `config`. `assertLive` runs before
 * each durable write is queued (a disposed client's write throws there).
 */
export function clientTransport(
  config: ClientTransport.Config,
  assertLive: (verb: string) => void,
): ClientTransport {
  let baseUrl = config.baseUrl;
  if (baseUrl?.endsWith("/")) baseUrl = baseUrl.slice(0, -1);
  // This client's debounced writes run on the host's executor, bound to
  // its origin and token — never on an executor another client installed
  // — and the same binding reports their refusals.
  const binding = config.debounceExecutor.bind({
    baseUrl: baseUrl ?? null,
    accessToken: config.accessToken ?? null,
    fetch: config.fetch ?? null,
  });

  const api = TypedApi.makeWith<Network.Routes>(
    Network.REVIVERS,
  ).withOptions({
    accessCode: config.accessToken,
    baseUrl,
    fetch: config.fetch,
    debounce: (debounceConfig) => {
      assertLive("a durable write");
      return binding.queue(debounceConfig);
    },
  });
  const requestErrorHandler = config.requestErrorHandler;
  api.delegate = {
    onUncaughtError: (failure, request) =>
      requestErrorHandler(tolerantRequestError(failure, request)),
  };
  const syncClient = new SyncClient(
    config.accessToken ?? null,
    baseUrl ?? null,
    config.fetch ?? null,
  );
  return { baseUrl: baseUrl ?? null, binding, api, syncClient };
}

/**
 * The sheet a refused debounced request updated, decoded from its URL
 * (`PATCH /api/sheets/{sheetKey}` — the one debounced sheet request,
 * `SheetsClient.updateDurable`); `null` for any other request.
 */
export function refusedSheetUpdateKey(url: string): Option<string> {
  const key = /\/api\/sheets\/([^/?]+)(?:\?|$)/.exec(url)?.[1];
  if (key == null) return null;
  return decodeURIComponent(key);
}

/**
 * The row a refused debounced request updated, decoded from its URL
 * (`PATCH /api/rows/{rowKey}` — `row.updateDurable`); `null` for any other
 * request (a row's `/cells` write included).
 */
export function refusedRowUpdateKey(url: string): Option<string> {
  const key = /\/api\/rows\/([^/?]+)(?:\?|$)/.exec(url)?.[1];
  if (key == null) return null;
  return decodeURIComponent(key);
}
